import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Link, Typography } from '@mui/material';

export default function TermsOfService() {
  useEffect(() => {
    document.title = 'Terms of Service — Nexus';
  }, []);

  return (
    <Box
      sx={{
        maxWidth: 672,
        mx: 'auto',
        p: 3,
        pb: 6,
        color: 'text.primary',
        '& ul': { pl: 2.5 },
        '& li': { mb: 0.75 },
      }}
    >
      <Typography variant="h4" component="h1" gutterBottom>
        Terms of Service
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        <strong>Effective date:</strong> March 30, 2026 · <strong>Service:</strong> Nexus (“Nexus”, “the
        Service”) operated by Digital Dynamic Solution (“we”, “us”).
      </Typography>
      <Typography paragraph>
        These Terms of Service (“Terms”) govern your access to and use of Nexus. By creating an account or
        using the Service, you agree to these Terms. If you do not agree, do not use Nexus.
      </Typography>

      <Typography variant="h6" component="h2" sx={{ mt: 3 }}>
        1. The Service
      </Typography>
      <Typography paragraph>
        Nexus provides tools to help you manage and publish content on connected social platforms, subject
        to feature availability and third-party rules. We may modify, suspend, or discontinue parts of the
        Service with reasonable notice where practicable.
      </Typography>

      <Typography variant="h6" component="h2" sx={{ mt: 3 }}>
        2. Eligibility and accounts
      </Typography>
      <Box component="ul">
        <Typography component="li" paragraph>
          You must be old enough to enter a binding contract in your jurisdiction and meet any minimum age
          required by the platforms you connect.
        </Typography>
        <Typography component="li" paragraph>
          You are responsible for keeping your login credentials confidential and for all activity under your
          account.
        </Typography>
        <Typography component="li" paragraph>
          You must provide accurate account information and keep it up to date.
        </Typography>
      </Box>

      <Typography variant="h6" component="h2" sx={{ mt: 3 }}>
        3. Acceptable use
      </Typography>
      <Typography paragraph>
        You agree not to misuse the Service. Without limitation, you must not: violate law; infringe others’
        rights; upload malware or attempt unauthorized access; scrape or overload our systems; impersonate
        others; or use the Service to spam, harass, or distribute unlawful content. We may suspend or
        terminate access for violations.
      </Typography>

      <Typography variant="h6" component="h2" sx={{ mt: 3 }}>
        4. Third-party platforms
      </Typography>
      <Typography paragraph>
        <strong>Nexus sign-in</strong> is <strong>email and password</strong> for your Nexus account.{' '}
        <strong>Meta (Facebook / Instagram)</strong> is <strong>optional</strong>: you use it to connect{' '}
        <strong>Facebook Pages and Instagram Business accounts</strong> for publishing and related
        features—<strong>not</strong> as your Nexus login.
      </Typography>
      <Typography paragraph>
        When you connect Facebook, Instagram, or other services, you also agree to their terms and policies.
        We do not control those platforms and are not responsible for their availability, features, or
        enforcement actions. You are responsible for content you publish and for complying with each platform’s
        rules.
      </Typography>

      <Typography variant="h6" component="h2" sx={{ mt: 3 }}>
        5. Your content
      </Typography>
      <Typography paragraph>
        You retain ownership of content you submit. You grant us a limited license to host, process, and
        display that content as needed to operate the Service (for example scheduling and publishing at your
        direction). You represent that you have the rights to grant this license.
      </Typography>

      <Typography variant="h6" component="h2" sx={{ mt: 3 }}>
        6. Our intellectual property
      </Typography>
      <Typography paragraph>
        The Service, including software, branding, and documentation, is owned by us or our licensors. Except
        as expressly allowed, you may not copy, modify, distribute, or reverse engineer the Service.
      </Typography>

      <Typography variant="h6" component="h2" sx={{ mt: 3 }}>
        7. Disclaimers
      </Typography>
      <Typography paragraph>
        The Service is provided “as is” and “as available.” To the fullest extent permitted by law, we
        disclaim warranties of merchantability, fitness for a particular purpose, and non-infringement. We do
        not guarantee uninterrupted or error-free operation or specific business results.
      </Typography>

      <Typography variant="h6" component="h2" sx={{ mt: 3 }}>
        8. Limitation of liability
      </Typography>
      <Typography paragraph>
        To the fullest extent permitted by law, we are not liable for indirect, incidental, special,
        consequential, or punitive damages, or for lost profits, data, or goodwill. Our total liability for
        claims arising from these Terms or the Service is limited to the greater of amounts you paid us for
        the Service in the twelve months before the claim or fifty U.S. dollars (USD $50), if any such fees
        apply.
      </Typography>

      <Typography variant="h6" component="h2" sx={{ mt: 3 }}>
        9. Indemnity
      </Typography>
      <Typography paragraph>
        You will defend and indemnify us and our affiliates against claims, damages, and expenses (including
        reasonable attorneys’ fees) arising from your use of the Service, your content, or your violation of
        these Terms or applicable law.
      </Typography>

      <Typography variant="h6" component="h2" sx={{ mt: 3 }}>
        10. Termination
      </Typography>
      <Typography paragraph>
        You may stop using the Service at any time. We may suspend or terminate your access if you breach
        these Terms, if required by law, or to protect the Service or other users. Provisions that by their
        nature should survive will survive termination.
      </Typography>

      <Typography variant="h6" component="h2" sx={{ mt: 3 }}>
        11. Changes to these Terms
      </Typography>
      <Typography paragraph>
        We may update these Terms by posting a new version on this page and updating the effective date.
        Continued use after changes constitutes acceptance of the updated Terms, except where applicable law
        requires additional notice or consent.
      </Typography>

      <Typography variant="h6" component="h2" sx={{ mt: 3 }}>
        12. General
      </Typography>
      <Typography paragraph>
        These Terms are governed by the laws of the United States and the State of Delaware, excluding
        conflict-of-law rules, unless mandatory consumer protections in your jurisdiction apply. If a
        provision is unenforceable, the remainder remains in effect.
      </Typography>

      <Typography variant="h6" component="h2" sx={{ mt: 3 }}>
        13. Privacy and data
      </Typography>
      <Typography paragraph>
        Our collection and use of personal information is described in our{' '}
        <Link component={RouterLink} to="/privacy-policy.html">
          Privacy Policy
        </Link>
        . Instructions for requesting deletion of certain data are in our{' '}
        <Link component={RouterLink} to="/data-deletion.html">
          User Data Deletion
        </Link>{' '}
        page.
      </Typography>

      <Typography variant="h6" component="h2" sx={{ mt: 3 }}>
        14. Contact
      </Typography>
      <Typography>
        For questions about these Terms, contact:{' '}
        <Link href="mailto:luthdigitalconsult@gmail.com">luthdigitalconsult@gmail.com</Link>
      </Typography>
    </Box>
  );
}
